import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Company } from '../../../../model/common/company.model';
import { Contact } from '../../../../model/common/contact.model';
import { GenericDataMapping, ValueType } from '../../../../model/common/generic-map-data.model';
import { Interaction } from '../../../../model/common/interaction.model';
import { ClientRepresentative } from '../../../../model/mail-classifier/client-representative.model';
import { UpdateLeadInfoDto } from '../../../../model/mail-classifier/update-lead-info.dto';
import { ConfigurationService } from '../../../../services/configuration.service';
import { MailReviewService } from '../../../../services/mail-review.service';
import { SnackBarService } from '../../../../services/snackbar/snackbar.service';
import { DatePipe } from '@angular/common';
import { HelperService } from '../../../../services/helper.service';

@Component({
  selector: 'app-mail-classifier-lead-interaction-item',
  templateUrl: './mail-classifier-lead-interaction-item.component.html',
  styleUrls: ['./mail-classifier-lead-interaction-item.component.scss'],
  standalone: false,
})
export class MailClassifierLeadInteractionItemComponent implements OnInit, OnChanges {
  @Input() interaction: Interaction;
  @Input() contact: Contact;
  @Input() company: Company;
  phoneCtrl = new FormControl('');
  detailsCollapsed = true;
  contactRows: GenericDataMapping[] = [];
  clientRepresentatives: ClientRepresentative = null;
  interactionRows: GenericDataMapping[] = [];
  valueType = ValueType;
  destroy$ = new Subject<boolean>();

  constructor(
    private configurationService: ConfigurationService,
    private mailReviewService: MailReviewService,
    private helperService: HelperService,
    private snackBarService: SnackBarService,
  ) {
    this.phoneCtrl.setValidators([Validators.pattern(this.helperService.getPhoneValidationPattern())]);
  }

  ngOnInit(): void {
    this.configurationService.clientRepresentatives$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (clientRepresentatives) => {
        this.clientRepresentatives = clientRepresentatives;
        this.setInteractionRows();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contact']) {
      this.setContactRows();
    }

    if (changes['interaction']) {
      this.setInteractionRows();
    }
  }

  setContactRows() {
    this.contactRows = [
      { title: 'Contact ID', value: this.contact?.contactId },
      { title: 'Contact Email', value: this.contact?.emailAddress },
      { title: 'Name', value: this.contact?.fullName },
      { title: 'Title', value: this.contact?.jobTitle },
      { title: 'Country', value: this.contact?.country },
      { title: 'State', value: this.contact?.adminArea1Code + ' | ' + this.contact?.adminArea1 },
      { title: 'City', value: this.contact?.adminArea2 },
      { title: 'Region', value: this.contact?.region },
      { title: 'Responsibility', value: this.contact?.responsibility },
      { title: 'Department', value: this.contact?.department },
      {
        title: 'In Lead',
        value: this.contact?.linkedInUrl ? `https://linkedin.com${this.contact?.linkedInUrl}` : '',
        type: this.contact?.linkedInUrl ? ValueType.Url : this.valueType.Text,
      },
      { title: 'Phones', value: this.contact?.phones },
      {
        title: 'To Contact Date',
        value: this.interaction?.toContact
          ? new DatePipe('en-US').transform(this.interaction?.toContact, 'yyyy-MM-dd')
          : '',
      },
    ];

    if (this.contact?.phones) {
      this.phoneCtrl.setValue(this.contact.phones);
    }
  }

  setInteractionRows() {
    this.interactionRows = [
      { title: 'Company', value: this.company?.companyName },
      {
        title: 'In Company',
        value: this.company?.linkedInUrl ? this.company?.linkedInUrl : '',
        type: this.company?.linkedInUrl ? ValueType.Url : this.valueType.Text,
      },
      { title: 'Country', value: this.company?.headquarters?.country },
      { title: 'Industry', value: this.company?.industry },
      { title: 'Size', value: this.company?.companySize },
      { title: 'Employees', value: this.company?.employeeCount },
      { title: 'Interaction ID', value: this.interaction?.interactionId },
      { title: 'Message ID', value: this.interaction?.messageId },
      { title: 'Sales Rep', value: this.interaction?.salesRepId || '' },
      {
        title: 'Email',
        value:
          this.clientRepresentatives?.representatives?.find((r) => r?.clientContactId === this.interaction?.salesRepId)
            ?.email || '',
      },
      { title: 'Type', value: this.interaction?.interactionType },
      { title: 'Source', value: this.interaction?.interactionSource },
    ];
  }

  handleUpdateLead() {
    if (!this.phoneCtrl.value || this.phoneCtrl.invalid) return;

    const {
      firstName: interactionFirstName,
      lastName: interactionLastName,
      jobTitle: interactionJobTitle,
    } = this.interaction;
    const {
      contactId,
      country,
      adminArea1,
      adminArea2,
      firstName: contactFirstName,
      lastName: contactLastName,
      jobTitle: contactJobTitle,
    } = this.contact;
    const {} = this.contact;
    const updateLeadInfoDto: UpdateLeadInfoDto = {
      firstName: interactionFirstName || contactFirstName,
      lastName: interactionLastName || contactLastName,
      jobTitle: interactionJobTitle || contactJobTitle,
      country,
      adminArea1,
      adminArea2,
      phone: this.phoneCtrl.value,
    };

    if (!contactId) {
      this.snackBarService.showError('An error occurred while retrieving the information required to update the lead');
      return;
    }

    this.updateLead(contactId, updateLeadInfoDto);
  }

  updateLead(contactId: number, updateLeadInfoDto: UpdateLeadInfoDto) {
    this.mailReviewService
      .updateLeadInfo(contactId, updateLeadInfoDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBarService.showSuccess('Contact information was updated successfully');
        },
        error: () => this.snackBarService.showError('An error occurred while updating contact information'),
      });
  }
}
